import type { LmsAdapter, MappingFormData } from './types';

/**
 * MentorKit LMS Pro (Norsk Interaktiv) — WordPress-basert
 *
 * Bruker webhook (push) istedenfor cron-polling. Datamapping mot studieplan
 * skjer via samme UI som Moodle/TutorLMS/LearnDash.
 */
export const mentorkitAdapter: LmsAdapter = {
  lmsType: 'mentorkit',
  displayName: 'MentorKit LMS Pro',
  shortDescription: 'Koble MentorKit-kurs (Norsk Interaktiv) til en godkjent studieplan',
  colors: {
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    badge: 'bg-sky-100 text-sky-800',
    primary: 'bg-sky-600 hover:bg-sky-700'
  },
  courseIdField: {
    label: 'MentorKit kurs-ID',
    placeholder: 'F.eks. 412',
    helpText: 'Kursets WordPress post-ID i MentorKit (vises i URL etter post= i WP-admin)',
    inputType: 'number',
    validate: (v) => /^\d+$/.test(v.trim()) ? null : 'Kurs-ID må være et heltall',
    parse: (v) => parseInt(v.trim(), 10)
  },
  courseNameField: {
    label: 'Kursnavn i MentorKit',
    placeholder: 'Eksakt tittel som vist i MentorKit-adminen',
    required: true
  },
  endpoints: {
    listMappingsForMember: (mid) => `/api/mentorkit/mappings/member/${mid}`,
    listMappingsForIntegration: (iid) => `/api/mentorkit/mappings/${iid}`,
    createMappingForMember: (mid) => `/api/mentorkit/mappings/member/${mid}`,
    updateMapping: (id) => `/api/mentorkit/mappings/${id}`,
    deleteMapping: (id) => `/api/mentorkit/mappings/${id}`,
    listCoursesByMember: (mid) => `/api/mentorkit/courses/member/${mid}`
  },
  normalizeCourseList: (rawCourses) =>
    (rawCourses as Array<{ id: number; title: string; slug?: string; status?: string }>)
      .map(c => ({
        id: String(c.id),
        name: c.title || `(uten tittel, ID ${c.id})`,
        secondary: [c.slug, c.status && c.status !== 'publish' ? c.status : null].filter(Boolean).join(' · ')
      })),
  buildCreateBody: (data: MappingFormData) => ({
    mk_course_id: parseInt(data.lmsCourseId, 10),
    mk_course_title: data.lmsCourseName,
    study_plan_id: data.studyPlanId,
    assessment_type: data.assessmentType,
    no_self_assessment_confirmed: data.noSelfAssessmentConfirmed,
    controlled_conditions_confirmed: data.controlledConditionsConfirmed,
    module_codes: data.moduleCodes.length > 0 ? data.moduleCodes : null,
    ects_credits: data.ectsCredits ?? null,
    certificate_title: data.certificateTitle ?? null,
    certificate_description: data.certificateDescription ?? null,
    framework_rationale: data.frameworkRationale || undefined,
    has_rubric: Boolean(data.hasRubric)
  }),
  buildUpdateBody: (data: MappingFormData) => ({
    mk_course_title: data.lmsCourseName,
    assessment_type: data.assessmentType,
    no_self_assessment_confirmed: data.noSelfAssessmentConfirmed,
    controlled_conditions_confirmed: data.controlledConditionsConfirmed,
    module_codes: data.moduleCodes.length > 0 ? data.moduleCodes : null,
    ects_credits: data.ectsCredits ?? null,
    certificate_title: data.certificateTitle ?? null,
    certificate_description: data.certificateDescription ?? null,
    framework_rationale: data.frameworkRationale || undefined,
    has_rubric: Boolean(data.hasRubric)
  })
};
